import React, { useState, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Input,
  useToast,
  Text,
  Checkbox,
  CheckboxGroup,
  Stack,
  Box,
  Divider,
  HStack,
  Badge,
  Spinner,
} from '@chakra-ui/react'
import { dataService } from '../services/api'

interface DeleteDataModalProps {
  isOpen: boolean
  onClose: () => void
  recordId: number
  symbols: string[]
  onSuccess?: () => void
}

const DeleteDataModal: React.FC<DeleteDataModalProps> = ({
  isOpen,
  onClose,
  recordId,
  symbols,
  onSuccess,
}) => {
  const toast = useToast()
  const [selectedSymbol, setSelectedSymbol] = useState<string>('')
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    // Set default date to today in YYYY-MM-DD format
    const today = new Date()
    return today.toISOString().split('T')[0]
  })
  const [availableHours, setAvailableHours] = useState<string[]>([])
  const [selectedHours, setSelectedHours] = useState<string[]>([])
  const [isLoadingHours, setIsLoadingHours] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [dataSource, setDataSource] = useState<string>('')

  const handleReset = () => {
    setSelectedSymbol('')
    // Reset date to today
    const today = new Date()
    setSelectedDate(today.toISOString().split('T')[0])
    setAvailableHours([])
    setSelectedHours([])
    setDataSource('')
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const handleGetHours = async () => {
    if (!selectedSymbol || !selectedDate) {
      toast({
        title: 'Please select Symbol and Date',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsLoadingHours(true)
    setAvailableHours([])
    setSelectedHours([])

    try {
      const response = await dataService.getHoursWithData({
        record_id: recordId,
        symbol: selectedSymbol,
        date: selectedDate,
      })

      const hours = response.data.hours || []
      
      if (hours.length === 0) {
        toast({
          title: 'No data for this date',
          description: 'Please select another date',
          status: 'info',
          duration: 3000,
          isClosable: true,
        })
      } else {
        setAvailableHours(hours)
        setDataSource(response.data.source)
        toast({
          title: 'Success',
          description: `Found ${hours.length} hour(s) of data`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to fetch hours',
        description:
          error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoadingHours(false)
    }
  }

  const handleDelete = async () => {
    if (selectedHours.length === 0) {
      toast({
        title: 'Please select hours to delete',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsDeleting(true)

    try {
      let totalDeleted = 0
      const errors: string[] = []

      // Delete each selected hour
      for (const hour of selectedHours) {
        try {
          const response = await dataService.deleteHourData({
            record_id: recordId,
            symbol: selectedSymbol,
            date: selectedDate,
            hour,
          })
          totalDeleted += response.data.deleted
        } catch (error) {
          errors.push(`Hour ${hour}: ${error instanceof Error ? error.message : 'Unknown error'}`)
        }
      }

      if (errors.length === 0) {
        toast({
          title: 'Deleted successfully',
          description: `Deleted ${totalDeleted} file(s)`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        handleClose()
        onSuccess?.()
      } else {
        toast({
          title: 'Partially deleted',
          description: `Successfully deleted ${totalDeleted} file(s), ${errors.length} hour(s) failed`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (error) {
      toast({
        title: 'Failed to delete',
        description:
          error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSelectAll = () => {
    if (selectedHours.length === availableHours.length) {
      setSelectedHours([])
    } else {
      setSelectedHours([...availableHours])
    }
  }

  const isAllSelected = useMemo(() => {
    return availableHours.length > 0 && selectedHours.length === availableHours.length
  }, [availableHours, selectedHours])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Data</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Symbol</FormLabel>
              <Select
                placeholder="Select Symbol"
                value={selectedSymbol}
                onChange={(e) => {
                  setSelectedSymbol(e.target.value)
                  setAvailableHours([])
                  setSelectedHours([])
                }}
              >
                <option value="all">All Symbol</option>
                {symbols.map((symbol) => (
                  <option key={symbol} value={symbol}>
                    {symbol}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value)
                  setAvailableHours([])
                  setSelectedHours([])
                }}
              />
            </FormControl>

            <Button
              colorScheme="blue"
              onClick={handleGetHours}
              isDisabled={!selectedSymbol || !selectedDate}
              isLoading={isLoadingHours}
              loadingText="Fetching..."
            >
              Get Hours List
            </Button>

            {availableHours.length > 0 && (
              <>
                <Divider />
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="bold">Select hours to delete</Text>
                    <HStack>
                      <Badge colorScheme="blue">{dataSource}</Badge>
                      <Button size="sm" variant="ghost" onClick={handleSelectAll}>
                        {isAllSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    </HStack>
                  </HStack>
                  <CheckboxGroup
                    value={selectedHours}
                    onChange={(values) => setSelectedHours(values as string[])}
                  >
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      spacing={2}
                      maxH="300px"
                      overflowY="auto"
                      p={2}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      {availableHours.map((hour) => (
                        <Checkbox key={hour} value={hour}>
                          {hour}:00
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Selected {selectedHours.length} hour(s)
                  </Text>
                </Box>
              </>
            )}

            {isLoadingHours && (
              <Box textAlign="center" py={4}>
                <Spinner size="lg" />
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleDelete}
            isDisabled={selectedHours.length === 0}
            isLoading={isDeleting}
            loadingText="Deleting..."
          >
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default DeleteDataModal
